import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import Select from "react-select";
import useEmblaCarousel from "embla-carousel-react";
import "../styles/Projects.css";


import GraphRate from "../assets/projects/CPIWageDisparityStatisticalAnalysis/GrowthRate.png";
import KeyQuestions from "../assets/projects/CPIWageDisparityStatisticalAnalysis/KeyQuestions .png";
import LorenzCurve from "../assets/projects/CPIWageDisparityStatisticalAnalysis/LorenzCurve.png";
import NominalVRealWage from "../assets/projects/CPIWageDisparityStatisticalAnalysis/NominalVRealWage.png";
import WageForecast from "../assets/projects/CPIWageDisparityStatisticalAnalysis/WageForecast.png";
import orpheus from "../assets/projects/orph/orpheus.png";


/* ---------- Modal gallery (manual: arrows/dots/keys) ---------- */
function ImageCarousel({ slides = [], startIndex = 0 }) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState([]);
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: "center", startIndex });

  const onSelect = useCallback(() => {
    if (emblaApi) setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  const prev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const next = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);
  const goTo = useCallback((i) => emblaApi && emblaApi.scrollTo(i), [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    setScrollSnaps(emblaApi.scrollSnapList());
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", () => {
      setScrollSnaps(emblaApi.scrollSnapList());
      onSelect();
    });
  }, [emblaApi, onSelect]);

  // Keyboard navigation only while the modal is open
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [prev, next]);

  if (!slides?.length) return null;

  return (
    <div className="embla">
      <div className="embla__viewport" ref={emblaRef}>
        <div className="embla__container">
          {slides.map((src, i) => (
            <div className="embla__slide" key={i}>
              <img className="embla__slide__img" src={src} alt={`Slide ${i + 1}`} loading="lazy" />
            </div>
          ))}
        </div>
      </div>
      <button type="button" className="embla__button embla__button--prev" onClick={prev} aria-label="Previous">‹</button>
      <button type="button" className="embla__button embla__button--next" onClick={next} aria-label="Next">›</button>
      <div className="embla__dots">
        {scrollSnaps.map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => goTo(i)}
            className={`embla__dot ${i === selectedIndex ? "is-selected" : ""}`}
            aria-label={`Go to slide ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
}

/* ---------- Modal (portal) ---------- */
function InlineModal({ open, onClose, children }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    document.body.classList.add("modal-open");
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.classList.remove("modal-open");
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <>
      <div
        className="project-modal-overlay"
        onClick={(e) => e.currentTarget === e.target && onClose?.()}
      />
      <div className="project-modal" role="dialog" aria-modal="true" aria-label="Project details">
        <button className="close-btn" onClick={onClose} aria-label="Close">×</button>
        {children}
      </div>
    </>,
    document.body
  );
}

/* ---------- Data (3 images each) ---------- */
const pics = (seed) => [
  `https://picsum.photos/seed/${seed}-1/1200/700`,
  `https://picsum.photos/seed/${seed}-2/1200/700`,
  `https://picsum.photos/seed/${seed}-3/1200/700`,
];

const projects = [
  {
    title: "Orpheus Analytics Platform",
    description: "End-to-end analytics engineering platform transforming public economic datasets into standardized, API-accessible data products. Designing ELT ingestion pipelines, OLAP-oriented data models, and a FastAPI + PostgreSQL + Docker delivery layer for enterprise analytics consumers. Platform in active architecture and development.",
    tags: ["Python", "FastAPI", "PostgreSQL", "Docker", "SQL", "ELT"],
    category: ["Data Engineering", "Analytics Engineering"],
    images: [orpheus],
    github: "https://github.com/OrpheusAnalytics"
  },

  {
    title: "Databricks Supply Chain Analytics Pipeline",
    description: "Built a zero-to-one supply chain analytics pipeline in 24 hours at HackUSU 2026 using Databricks ingested planning datasets, transformed with SQL and Python, deployed a Streamlit application enabling dynamic querying and rapid analysis of sourcing and demand signals.",
    tags: ["Databricks", "Python", "SQL", "Streamlit", "Apache Spark"],
    category: ["Data Engineering", "Analytics Engineering"],
    images: pics("databricks"),
    github: "https://github.com/ChristianLG2"
  },

  {
    title: "CPI Wage Disparity Statistical Analysis",
    description: "Econometric analysis of CPI and wage dynamics across all U.S. states (2015–2023). Applied ANOVA, regression, Gini index, and ARIMA forecasting in R to quantify real wage erosion, geographic purchasing power disparities, and wage inequality trends. Findings: no statistically significant correlation between minimum wage increases and unemployment across target populations.",
    tags: ["R", "HTML", "ARIMA", "Regression", "ANOVA"],
    category: ["Data Science", "Economics"],
    images: [NominalVRealWage, GraphRate, LorenzCurve, WageForecast, KeyQuestions],
    github: "https://github.com/ChristianLG2/CPI-Wage-Disparity-Statistical-Analysis"
  },

  {
    title: "NLP Topic Modeler",
    description: "Unsupervised machine learning pipeline analyzing large-scale open-ended survey responses. Applied LDA topic modeling to automatically extract key themes from text data, enhanced with sentiment analysis (VADER) and statistical validation across 1,000 simulations. Built for BYU-Idaho research across ~120 online programs.",
    tags: ["Python", "NLP", "LDA", "NumPy", "Jupyter Notebooks"],
    category: ["Machine Learning", "Data Science"],
    images: pics("nlp"),
    github: "https://github.com/ChristianLG2/NLP_Topic_Modeler"
  },

  {
  title: "Vendor & Sales Operations Analytics",
  description: "T-SQL analytics on the Northwind database answering key operational questions: which suppliers drive the most revenue, how has monthly revenue trended, and where are fulfillment delays? Demonstrates window functions (RANK, LAG, rolling averages), CTEs, and period-over-period analysis. Includes Python + pandas + matplotlib visualization layer.",
  tags: ["SQL", "T-SQL", "Python", "pandas", "matplotlib", "SSMS"],
  category: ["Analytics Engineering", "Data Science"],
  images: pics("sql-vendor"),
  github: "https://github.com/ChristianLG2/sql-analytics-portfolio/tree/main/01_vendor_sales_analytics"
},

{
  title: "Customer Cohort Retention Analysis",
  description: "Pure SQL cohort retention analysis on Northwind customer data, the single most requested skill in analytics engineering interviews. Built using a 3-CTE pattern to assign cohorts, track monthly activity, and calculate retention rates. Output: seaborn heatmap showing retention by cohort month.",
  tags: ["SQL", "T-SQL", "Python", "seaborn", "SSMS"],
  category: ["Analytics Engineering", "Data Science"],
  images: pics("sql-cohort"),
  github: "https://github.com/ChristianLG2/sql-analytics-portfolio/tree/main/02_cohort_retention"
},

{
  title: "Query Optimization Showcase",
  description: "Three real optimization scenarios on Northwind data, missing index diagnosis, correlated subquery rewrite to CTE, and function-on-indexed-column anti-pattern. Each with before/after SSMS execution plans and written explanation of the performance principle. Separates 'can write SQL' from 'thinks like an engineer.'",
  tags: ["SQL", "T-SQL", "SSMS", "Query Optimization", "Indexing"],
  category: ["Analytics Engineering", "Data Engineering"],
  images: pics("sql-optimization"),
  github: "https://github.com/ChristianLG2/sql-analytics-portfolio/tree/main/03_query_optimization"
},

{
  title: "Star Schema Design + Power BI",
  description: "Full analytics engineering workflow: redesigned Northwind's normalized OLTP schema into a dimensional star schema (fact_OrderItems + 6 dimensions), migrated data via T-SQL ETL, and built a Power BI semantic layer with DAX measures and two report pages. Mirrors the BYU-Idaho OLTP-to-OLAP architecture work.",
  tags: ["SQL", "T-SQL", "Power BI", "DAX", "Star Schema", "ETL"],
  category: ["Analytics Engineering", "Data Engineering"],
  images: pics("sql-star"),
  github: "https://github.com/ChristianLG2/sql-analytics-portfolio/tree/main/04_star_schema_power_bi"
},

  {
    title: "GradeBase",
    description: "Designed and built a normalized 13-table relational database system in SQLite with Python seeding scripts, SQL query layer, and a Streamlit analytics dashboard for academic performance tracking, demonstrating intentional schema design, normalization, and data-driven reporting.",
    tags: ["SQL", "Python", "Streamlit", "SQLite"],
    category: ["Data Engineering", "Data Science"],
    images: pics("sql"),
    github: "https://github.com/ChristianLG2/GradeBase"
  },

  {
    title: "Global Air Quality ArcGIS",
    description: "Interactive web-based map visualizing air quality measurements worldwide using data from the OpenAQ API. Supports pollutant filtering, intuitive color scaling, and dynamic marker sizing, combining data engineering, geospatial rendering, and web development.",
    tags: ["JavaScript", "ArcGIS", "Python", "API"],
    category: ["Data Engineering", "Full-Stack Web Dev"],
    images: pics("ml"),
    github: "https://github.com/ChristianLG2/Global-Air-Quality-ArcGIS"
  },

  {
    title: "DaoFocus",
    description: "A mindful Pomodoro app inspired by the 道德经 (Dao De Jing), helping programmers, students, and workers balance focus and rest through natural cycles.",
    tags: ["React", "Firebase", "HTML", "CSS", "JavaScript"],
    category: ["Full-Stack Web Dev"],
    images: pics("portfolio"),
    github: "https://github.com/ChristianLG2/DaoFocus"
  },
];

const INITIAL_COUNT = 4;
const STEP = 2;

export default function Projects() {
  // Select options
  const toolOptions = useMemo(() => {
    const all = Array.from(new Set(projects.flatMap((p) => p.tags)));
    return all.sort().map((t) => ({ label: t, value: t }));
  }, []);
  const catOptions = useMemo(() => {
    const all = Array.from(new Set(projects.flatMap((p) => p.category)));
    return all.sort().map((c) => ({ label: c, value: c }));
  }, []);

  // State
  const [tools, setTools] = useState([]);
  const [cats, setCats] = useState([]);
  const [active, setActive] = useState(null);
  const [visible, setVisible] = useState(INITIAL_COUNT);

  // Filtering
  const selectedTools = useMemo(() => tools.map(t => t.value), [tools]);
  const selectedCats  = useMemo(() => cats.map(c => c.value), [cats]);

  const filtered = useMemo(() => {
    return projects.filter((p) => {
      const toolOK = !selectedTools.length || selectedTools.every((t) => p.tags.includes(t));
      const catOK = !selectedCats.length || selectedCats.every((c) => p.category.includes(c));
      return toolOK && catOK;
    });
  }, [selectedTools, selectedCats]);

  // Reset visible when filters change
  useEffect(() => {
  setVisible(v => Math.min(Math.max(v, INITIAL_COUNT), filtered.length));
}, [selectedTools, selectedCats, filtered.length]);


  // Smooth scroll when adding rows
  const endRef = useRef(null);
  const prevVisibleRef = useRef(visible);
  useEffect(() => {
    if (visible > prevVisibleRef.current) {
      endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    }
    prevVisibleRef.current = visible;
  }, [visible]);

  const showMore = useCallback(() => {
    setVisible((v) => Math.min(v + STEP, filtered.length));
  }, [filtered.length]);
  const showAll = useCallback(() => setVisible(filtered.length), [filtered.length]);
  const showLess = useCallback(
    () => setVisible(Math.min(INITIAL_COUNT, filtered.length)),
    [filtered.length]
  );
  const clearFilters = useCallback(() => { setTools([]); setCats([]); }, []);

  const shown = filtered.slice(0, visible);
  const portalZ = { menuPortal: (base) => ({ ...base, zIndex: 9999 }) };

  // Ensure ≥3 images in modal
  const modalImages = useMemo(() => {
    if (!active) return [];
    const imgs = Array.isArray(active.images) ? active.images.slice() : [];
    while (imgs.length < 3 && imgs.length > 0) imgs.push(imgs[0]);
    return imgs;
  }, [active]);

  return (
    <section id="projects" className="page-projects">
      <div className="projects-frame">
        <div className="projects-inner">
          <header className="projects-header">
            <h2 className="section-title">Projects</h2>
          </header>

          {/* Filters */}
          <div className="filter-bar">
            <div className="filter-item tool">
              <Select
                isMulti
                placeholder="Filter by Tool"
                options={toolOptions}
                value={tools}
                onChange={setTools}
                className="react-select-container"
                classNamePrefix="rs"
                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                styles={portalZ}
                menuPosition="fixed"
              />
            </div>

            <div className="filter-item cat">
              <Select
                isMulti
                placeholder="Filter by Category"
                options={catOptions}
                value={cats}
                onChange={setCats}
                className="react-select-container"
                classNamePrefix="rs"
                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                styles={portalZ}
                menuPosition="fixed"
              />
            </div>

            <button type="button" className="clear-filters" onClick={clearFilters}>
              Clear
            </button>
          </div>

          {/* Cards */}
          <div className="projects-grid">
            {shown.length === 0 && <div className="no-results">No results. Try clearing filters.</div>}

            {shown.map((p) => (
              <article
                key={p.title}
                className="project-card"
                role="button"
                tabIndex={0}
                onClick={(e) => { setActive(p); e.currentTarget.blur(); }}
                onKeyDown={(e) => e.key === "Enter" && setActive(p)}
              >
                <div className="project-thumb">
                  <img src={p.images[0]} alt={`${p.title} cover`} loading="lazy" />
                </div>
                <h3>{p.title}</h3>
                <p>{p.description}</p>
                <div className="project-card__tags">
                  {p.tags.map((t) => <span className="tag" key={t}>{t}</span>)}
                </div>
                <a
                  className="project-card__link"
                  href={p.github}
                  target="_blank"
                  rel="noreferrer"
                  onClick={(e) => e.stopPropagation()}
                >
                  View on GitHub
                </a>
              </article>
            ))}
          </div>

          {/* sentinel for smooth scroll after loading more */}
          <div ref={endRef} aria-hidden="true" style={{ height: 1 }} />

          {/* Load controls */}
          {filtered.length > 0 && (
            <div className="load-controls">
              <span className="load-count">{visible} / {filtered.length} shown</span>
              {visible < filtered.length && (
                <>
                  <button type="button" className="load-btn" onClick={showMore}>Load 2 more</button>
                  <button type="button" className="load-btn ghost" onClick={showAll}>Load all</button>
                </>
              )}
              {visible > Math.min(INITIAL_COUNT, filtered.length) && (
                <button type="button" className="load-btn ghost" onClick={showLess}>Show less</button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      <InlineModal open={!!active} onClose={() => setActive(null)}>
        {active && (
          <>
            <h3 style={{ marginTop: 0, textAlign: "center" }}>{active.title}</h3>
            <p className="project-desc">{active.description}</p>
            <div className="project-card__tags" style={{ marginBottom: "0.75rem" }}>
              {active.tags.map((t) => <span className="tag" key={t}>{t}</span>)}
            </div>
            <ImageCarousel slides={modalImages} />
            <a className="github-link" href={active.github} target="_blank" rel="noreferrer" style={{ marginTop: "4rem"}}>
              View Repo
            </a>
          </>
        )}
      </InlineModal>
    </section>
  );
}
















